import { useApolloClient, useQuery, useReactiveVar } from '@apollo/client'
import { ArrowDropDown, FileCopyOutlined } from '@mui/icons-material'
import { Box, Button, Menu, MenuItem, useTheme } from '@mui/material'
import _, { head, startCase } from 'lodash'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { isIOS, isSafari } from 'react-device-detect'
import { useParams } from 'react-router-dom'
import { useCopyToClipboard } from 'react-use'
import { baseDomain } from '../../../../config'
import { BucketInvitationQueryDocument, BucketInvitationsDocument, BucketType, BucketUserRole } from '../../../../graphql/generated'
import { activeBucketIdVar, modalVar } from '../../../../providers/apollo/cache'
import { InvitationContext } from '../../InvitationContext'
import useAlerts from '../../hooks/useAlerts'
import useBucket from '../../hooks/useBucket'
import useBucketInvitationCreate from '../../hooks/useBucketInvitationCreate'
import useBucketPlan from '../../hooks/useBucketPlan'
import useBucketUpdate from '../../hooks/useBucketUpdate'
import useGetBucketUsersAndInvitations from '../../hooks/useGetBucketUsersAndInviations'

export default function CopyLink() {
  const theme = useTheme()
  const { createAlert } = useAlerts()
  const { cache } = useApolloClient()
  const bucketIdParam = useReactiveVar(activeBucketIdVar)
  const { bucket, isDefaultTitle, bucketUser } = useBucket(undefined, bucketIdParam ?? undefined)
  const { bucketId: bucketIdQueryParam } = useParams<{ bucketId: string }>()
  const { genericInvitations } = useGetBucketUsersAndInvitations()
  const [userRole, setUserRole] = useState<BucketUserRole | null>(null)
  const { inviteCache, setInviteCache } = useContext(InvitationContext)

  const bucketId = bucketIdParam ?? bucketIdQueryParam

  const [copyState, copyToClipboard] = useCopyToClipboard()
  const [copiedValue, setCopiedValue] = useState<string | null>(null)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const { mutation } = useBucketInvitationCreate()
  const { isSharingPlan } = useBucketPlan()
  const { mutation: mutationBucketUpdate } = useBucketUpdate()

  const genericViewerInvitation = genericInvitations?.find((item) => item.userRole === BucketUserRole.Viewer)
  const genericContributorInvitation = genericInvitations?.find((item) => item.userRole === BucketUserRole.Contributor)

  const userIsAdminOrOwner = bucketUser?.userRole === BucketUserRole.Admin || bucketUser?.userRole === BucketUserRole.Owner

  const items = useMemo(() => {
    if (!bucketUser?.userRole) {
      return []
    }
    if (bucketUser?.userRole === BucketUserRole.Viewer) {
      return genericViewerInvitation ? [BucketUserRole.Viewer] : []
    }
    if (bucketUser?.userRole === BucketUserRole.Contributor) {
      const permissions = []
      if (genericContributorInvitation) {
        permissions.push(BucketUserRole.Contributor)
      }
      if (genericViewerInvitation) {
        permissions.push(BucketUserRole.Viewer)
      }
      return permissions
    }
    return [BucketUserRole.Contributor, BucketUserRole.Viewer, BucketUserRole.Admin]
  }, [bucketUser, genericContributorInvitation, genericViewerInvitation])

  useEffect(() => {
    if (userIsAdminOrOwner) {
      setUserRole(BucketUserRole.Contributor)
    } else if (bucketUser?.userRole === BucketUserRole.Contributor && items.includes(BucketUserRole.Contributor)) {
      setUserRole(BucketUserRole.Contributor)
    } else if (items.includes(BucketUserRole.Viewer)) {
      setUserRole(BucketUserRole.Viewer)
    }
  }, [bucketUser?.userRole, items, userIsAdminOrOwner])

  const disableCopyButton = !userIsAdminOrOwner && !items.length

  let copy = 'Copy Link'
  let create = 'Create & Copy Link'

  if (isIOS || isSafari) {
    copy = 'Copy Link'
    create = 'Create Link'
  }

  useEffect(() => {
    if (!userRole || !inviteCache[userRole]) {
      setCopiedValue(null)
    }
  }, [inviteCache, userRole])

  useEffect(() => {
    if (copyState.error) {
      createAlert(copyState.error.message, 'error')
    }
  }, [copyState.error, createAlert])

  const addInviteToCache = (role: BucketUserRole, id: string) => {
    const updatedCache = { ...inviteCache }
    updatedCache[role] = id
    setInviteCache(updatedCache)
  }

  useQuery(BucketInvitationQueryDocument, {
    skip: !bucketId || !userRole,
    variables: { bucketId: bucketId!, userRole: userRole! },
    onError: (e) => {
      e.graphQLErrors.forEach((error) => {
        createAlert(error.message, 'error', error)
      })
    },
    onCompleted: (bucketInvitation) => {
      const results = bucketInvitation?.bucketInvitationQuery?.items || []
      const invitation = head(results)
      if (invitation) {
        addInviteToCache(invitation.userRole, invitation.id)
      }
    }
  })

  const handleClickDropdown = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseDropdown = () => {
    setAnchorEl(null)
  }

  const handleOption = (role: BucketUserRole) => {
    return () => {
      const cachedCopyValue = inviteCache[role]
      if (cachedCopyValue) {
        const text = `https://${baseDomain}/invite/${cachedCopyValue}`
        setCopiedValue(text)
      } else {
        setCopiedValue(null)
      }
      setAnchorEl(null)
      setUserRole(role)
    }
  }

  const handleCopy = async () => {
    if (!bucketId) {
      return
    }

    if (bucket?.bucketType === BucketType.Mybucket) {
      if (isSharingPlan && !isDefaultTitle) {
        mutationBucketUpdate({
          variables: {
            bucketId: bucket?.id || '',
            data: { bucketType: BucketType.Group }
          }
        })
      } else {
        modalVar('bucketUpgrade')
        return
      }
    }

    // If user pressed the copy button multiple times
    if (copiedValue) {
      copyToClipboard(copiedValue)
      createAlert('Link copied to clipboard', 'success')
      return
    }
    if ([BucketUserRole.Viewer, BucketUserRole.Contributor].includes(bucketUser?.userRole ?? BucketUserRole.Owner)) {
      if (userRole === BucketUserRole.Viewer && genericViewerInvitation) {
        const text = `https://${baseDomain}/invite/${genericViewerInvitation.id}`
        copyToClipboard(text)
        setCopiedValue(text)
        createAlert('Link copied to clipboard', 'success')
        return
      }
      if (userRole === BucketUserRole.Contributor && genericContributorInvitation) {
        const text = `https://${baseDomain}/invite/${genericContributorInvitation.id}`
        copyToClipboard(text)
        setCopiedValue(text)
        createAlert('Link copied to clipboard', 'success')
        return
      }
    }

    if (userRole) {
      mutation({
        variables: { bucketId, userRole },
        onCompleted: async ({ bucketInvitationCreate }) => {
          cache.updateQuery({ query: BucketInvitationsDocument, variables: { bucketId, nextToken: null } }, (data) => {
            if (!data || !bucketInvitationCreate) return null
            const { bucketInvitations } = data
            const results = data?.bucketInvitations?.items || []
            const combinedItems = _.concat(results, bucketInvitationCreate)

            const sortedItems = [...combinedItems].sort((a, b) => {
              if (!a.createdAt || !b.createdAt) return 0
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            })

            return {
              bucketInvitations: {
                ...bucketInvitations,
                items: sortedItems
              }
            }
          })

          const invitation = head(bucketInvitationCreate)
          let text
          if (invitation) {
            addInviteToCache(invitation.userRole, invitation.id)
            text = `https://${baseDomain}/invite/${invitation.id}`
            setCopiedValue(text)
            // safari browers and ios devices block copy if not a direct input
            const apple = isIOS || isSafari
            if (!apple) {
              copyToClipboard(text)
              createAlert('Link copied to clipboard', 'success')
            }
          }
        },
        onError: (e) => {
          e.graphQLErrors.forEach((error) => {
            createAlert(error.message, 'error', error)
          })
        }
      })
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
      <Button
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        disabled={disableCopyButton}
        onClick={handleClickDropdown}
        variant="outlined"
        sx={{
          flex: 1,
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          justifyContent: 'space-between',
          '&:hover': {
            borderColor: theme.palette.primary.dark,
            backgroundColor: 'transparent'
          }
        }}
      >
        {!disableCopyButton && !!userRole && startCase(userRole.toLowerCase())}
        <ArrowDropDown htmlColor={theme.palette.primary.main} />
      </Button>

      <Button
        variant="outlined"
        startIcon={<FileCopyOutlined />}
        onClick={handleCopy}
        disabled={disableCopyButton}
        sx={{
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          borderRadius: 10,
          '&:hover': {
            borderColor: theme.palette.primary.dark,
            backgroundColor: 'transparent'
          }
        }}
      >
        {copiedValue ? copy : create}
      </Button>

      <Menu
        id="copy-link-role-menu"
        MenuListProps={{
          'aria-labelledby': 'copy-link-role-menu'
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseDropdown}
        PaperProps={{
          style: {
            maxHeight: 48 * 4.5
          }
        }}
      >
        {items.map((option) => (
          <MenuItem key={option} onClick={handleOption(option)}>
            {startCase(option.toLowerCase())}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}
